import { Circle, Container, Graphics, Rectangle, Sprite, Text, type Texture } from 'pixi.js';
import { BrassButton, COLOURS, displayStyle, uiStyle } from '../board';
import { ARROW_GAP, DOT_STEP, DOTS_H, PLATE_W, arrowD } from '../../logic/realm/mapHud';
import { floorPlate } from './RealmPieces';

/** An arrow takes taps this far past its disc: 56 px across at the smallest. */
const ARROW_TAP_PAD = 6;

/**
 * The strip's chosen-world panel: a plate on the world's floor with its name (Lilita One) and "{open} of 7 places open" (Fredoka),
 * the Visit button under it, and an arrow either side that steps one world west or east: beside the plate, or beside the Visit button
 * where the screen is too narrow. Every world's plate is one width (the widest name's), so the arrows stay put from world to world.
 * Its origin is the plate's top middle, so the scene places it by one spot under the chosen scene.
 */
export class OverviewPanel extends Container {
  private readonly plate = new Graphics();
  private readonly nameText: Text;
  private readonly lineText: Text;
  private readonly visit: BrassButton;
  private readonly west: StripArrow;
  private readonly east: StripArrow;
  private floor: Texture | null = null;
  private compact: boolean;
  private beside = true;
  private widest = 0;
  /** The words shown (tests). */
  title = '';
  line = '';

  constructor(
    floor: Texture | null,
    compact: boolean,
    onVisit: () => void,
    private readonly names: readonly string[],
    arrowArt: Texture | null,
    onStep: (dir: -1 | 1) => void,
  ) {
    super();
    this.floor = floor;
    this.compact = compact;
    this.nameText = new Text({ text: '', style: displayStyle(compact ? 20 : 26) });
    this.nameText.anchor.set(0.5);
    this.lineText = new Text({ text: '', style: uiStyle(compact ? 12 : 14, 0xf3e7cf, '600') });
    this.lineText.anchor.set(0.5);
    this.visit = new BrassButton({
      label: 'Visit',
      kind: 'gold',
      width: compact ? 120 : 150,
      height: compact ? 38 : 46,
      size: compact ? 18 : 22,
      onTap: onVisit,
    });
    this.visit.label = 'visit';
    this.west = new StripArrow('west', arrowArt, () => onStep(-1));
    this.east = new StripArrow('east', arrowArt, () => onStep(1));
    this.addChild(this.plate, this.nameText, this.lineText, this.visit, this.west, this.east);
    this.measureNames();
    this.layout();
  }

  /** The arrow buttons (tests find them by their labels). */
  arrow(dir: -1 | 1): Container {
    return dir < 0 ? this.west : this.east;
  }

  /** Which arrows show (none past the road's ends), and where they stand. */
  arrows(): { west: boolean; east: boolean; beside: boolean } {
    return { west: this.west.shown, east: this.east.shown, beside: this.beside };
  }

  /** Show the arrows that lead somewhere: none west of the first world, none east of the last. */
  setEnds(west: boolean, east: boolean): void {
    this.west.setShown(west);
    this.east.setShown(east);
  }

  /** Stand the arrows beside the plate (a wide screen) or beside the Visit button (a narrow one). */
  setArrowsBeside(beside: boolean): void {
    if (beside === this.beside) return;
    this.beside = beside;
    this.layout();
  }

  /** The visit button (tests find it by its label). */
  get visitButton(): Container {
    return this.visit;
  }

  set(name: string, open: number, total: number): void {
    this.title = name;
    this.line = `${open} of ${total} places open`;
    this.nameText.text = name;
    this.lineText.text = this.line;
    this.layout();
  }

  setFloor(texture: Texture | null): void {
    if (!texture || texture === this.floor || this.destroyed) return;
    this.floor = texture;
    this.layout();
  }

  get floorTexture(): Texture | null {
    return this.floor;
  }

  /** Shorter on a short screen. */
  setCompact(compact: boolean): void {
    if (compact === this.compact) return;
    this.compact = compact;
    this.nameText.style = displayStyle(compact ? 20 : 26);
    this.lineText.style = uiStyle(compact ? 12 : 14, 0xf3e7cf, '600');
    this.measureNames();
    this.layout();
  }

  /** The widest of the seven names at the name's size: every plate is drawn to fit it. */
  private measureNames(): void {
    const was = this.nameText.text;
    this.widest = 0;
    for (const n of this.names) {
      this.nameText.text = n;
      this.widest = Math.max(this.widest, this.nameText.width);
    }
    this.nameText.text = was;
  }

  private layout(): void {
    const c = this.compact;
    const padX = c ? 22 : 30;
    const w = Math.min(PLATE_W, Math.max(c ? 150 : 200, Math.max(this.widest, this.nameText.width, this.lineText.width) + padX * 2));
    const h = c ? 52 : 62;
    this.nameText.position.set(0, c ? 18 : 22);
    this.lineText.position.set(0, c ? 39 : 47);
    this.plate.clear();
    floorPlate(this.plate, { x: -w / 2, y: 0, w, h }, this.floor, { edge: COLOURS.brass, edgeWidth: 2 });
    const visitY = h + (c ? 6 : 10);
    const visitH = c ? 38 : 46;
    this.visit.position.set(-this.visit.buttonWidth / 2, visitY);
    const d = arrowD(c);
    const [ax, ay] = this.beside ? [w / 2 + ARROW_GAP + d / 2, h / 2] : [this.visit.buttonWidth / 2 + ARROW_GAP + d / 2, visitY + visitH / 2];
    for (const a of [this.west, this.east]) a.setSize(d);
    this.west.position.set(-ax, ay);
    this.east.position.set(ax, ay);
    // The hit area holds the arrows' whole tap circles (their discs and ARROW_TAP_PAD round them): the hit test passes by what is outside it.
    const r = d / 2 + ARROW_TAP_PAD;
    const half = Math.max(w / 2, ax + r);
    const top = Math.min(0, ay - r);
    this.hitArea = new Rectangle(-half, top, half * 2, Math.max(visitY + visitH, ay + r) - top);
  }
}

/** The phone dots: one a world along the road, the chosen one larger and lit. Not flat discs: a dark rim, a body and a highlight, and
 *  a soft glow behind the chosen. */
export class StripDots extends Container {
  private readonly dots: Graphics[];
  private chosen = -1;

  constructor(count: number, onTap: (i: number) => void) {
    super();
    this.dots = Array.from({ length: count }, (_, i) => {
      const slot = new Container();
      slot.position.set(i * DOT_STEP, 0);
      slot.eventMode = 'static';
      slot.cursor = 'pointer';
      slot.hitArea = new Rectangle(-DOT_STEP / 2, -DOTS_H / 2 - 6, DOT_STEP, DOTS_H + 12);
      slot.on('pointertap', () => onTap(i));
      slot.label = `dot:${i}`;
      const g = new Graphics();
      slot.addChild(g);
      this.addChild(slot);
      return g;
    });
    this.draw();
  }

  get stride(): number {
    return DOT_STEP;
  }

  setChosen(i: number): void {
    if (i === this.chosen) return;
    this.chosen = i;
    this.draw();
  }

  /** Where dot `i`'s middle is, in this container's own space. */
  dotAt(i: number): { x: number; y: number } {
    return { x: i * DOT_STEP, y: 0 };
  }

  private draw(): void {
    this.dots.forEach((g, i) => {
      g.clear();
      const on = i === this.chosen;
      if (on) {
        for (const [r, a] of [[11, 0.16], [8.5, 0.3]] as const) g.circle(0, 0, r).fill({ color: 0xffd25a, alpha: a });
      }
      const r = on ? 6.5 : 4.5;
      g.circle(0, 1, r + 1.5).fill({ color: 0x000000, alpha: 0.25 });
      g.circle(0, 0, r + 1).fill({ color: COLOURS.ink, alpha: 0.85 });
      g.circle(0, 0, r).fill({ color: on ? 0xf0b838 : 0xfff3d8 });
      g.circle(-r * 0.3, -r * 0.35, r * 0.4).fill({ color: 0xffffff, alpha: on ? 0.7 : 0.55 });
    });
  }
}

/** One of the panel's arrows: a round dark plate with a brass rim and the switcher's arrow art, a step west or east. Its origin is its
 *  middle. Hidden (past the road's end) it keeps its place, so the panel does not shift. */
export class StripArrow extends Container {
  private readonly disc = new Graphics();
  private readonly art: Sprite | null;
  /** It leads somewhere (tests). */
  shown = true;

  constructor(
    private readonly side: 'west' | 'east',
    art: Texture | null,
    onTap: () => void,
  ) {
    super();
    this.art = art ? new Sprite(art) : null;
    this.addChild(this.disc);
    if (this.art) {
      this.art.anchor.set(0.5);
      this.addChild(this.art);
    }
    this.label = `arrow:${side}`;
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', () => {
      if (this.shown) onTap();
    });
    this.setSize(arrowD(false));
  }

  setSize(d: number): void {
    const r = d / 2;
    this.disc.clear();
    this.disc.circle(0, 2, r + 1).fill({ color: 0x000000, alpha: 0.28 });
    this.disc.circle(0, 0, r).fill({ color: 0x2a1c10, alpha: 0.94 });
    this.disc.circle(0, 0, r).stroke({ color: COLOURS.brass, width: 2.5 });
    this.disc.circle(0, 0, r - 4).stroke({ color: 0xf3e3bc, width: 1, alpha: 0.18 });
    if (this.art) {
      const t = this.art.texture;
      // The art points west: the east arrow is its mirror.
      this.art.scale.set((d * 0.56) / Math.max(t.width, t.height));
      if (this.side === 'east') this.art.scale.x *= -1;
    }
    this.hitArea = new Circle(0, 0, r + ARROW_TAP_PAD);
  }

  setShown(on: boolean): void {
    this.shown = on;
    this.alpha = on ? 1 : 0;
    this.eventMode = on ? 'static' : 'none';
  }
}

/** A one-line note over the strip (what a lamp means): on a plate, in screen pixels, fading out after a few seconds. */
export class StripNote extends Container {
  private readonly plate = new Graphics();
  private readonly text = new Text({ text: '', style: uiStyle(14, 0xf3e7cf, '600') });
  private left = 0;
  /** The line now shown, or '' (tests). */
  line = '';

  constructor() {
    super();
    this.text.anchor.set(0.5);
    this.addChild(this.plate, this.text);
    this.visible = false;
    this.eventMode = 'none';
  }

  /** Show `line` above the screen point `at`, kept inside a screen `view` wide, for about three seconds. */
  say(line: string, at: { x: number; y: number }, view: { w: number; h: number }, floor: Texture | null): void {
    this.line = line;
    this.text.text = line;
    this.text.style.wordWrap = true;
    this.text.style.wordWrapWidth = Math.min(260, view.w - 48);
    const w = this.text.width + 28;
    const h = this.text.height + 18;
    this.plate.clear();
    floorPlate(this.plate, { x: -w / 2, y: -h / 2, w, h }, floor, { edge: COLOURS.brass, edgeWidth: 1.5 });
    this.position.set(Math.max(w / 2 + 8, Math.min(view.w - w / 2 - 8, at.x)), Math.max(h / 2 + 70, at.y - h / 2 - 16));
    this.alpha = 1;
    this.visible = true;
    this.left = 3000;
  }

  update(ms: number): void {
    if (!this.visible) return;
    this.left -= ms;
    if (this.left < 400) this.alpha = Math.max(0, this.left / 400);
    if (this.left <= 0) {
      this.visible = false;
      this.line = '';
    }
  }
}
